import formStyles from "./UserCreationForm.module.css"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import Dropzone from "react-dropzone"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import TextareaAutosize from 'react-textarea-autosize'
import api from "../api"
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-plus.svg"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-image-edit.svg"
import { ReactComponent as InsertImageIllustration } from "../assets/images/illustrations/il-insert-image.svg"
import { AccountStatus, ButtonStatus, Role } from "../common/constants"
import { Each } from "../common/Each"
import useWindowDimensions from "../common/hooks/useWindowDimensions"
import { capitalize, sortUser } from "../utils"
import StatusBadge from "./StatusBadge"
import Button from "./Button"
import Dialog from "./dialogs/Dialog"
import TagSelector from "./TagSelector"
import ImageCropper from "./ImageCropper"
import Pagination from "./Pagination"
import Table from "./Table"
import styles from "./Teachers.module.css"
import TextInput from "./TextInput"
import TooltipWrapper from "./TooltipWrapper"

const Teachers = forwardRef(({ role = Role.Teacher, orderBy, sortDirection }, ref) => {
    const { t } = useTranslation()
    const { width } = useWindowDimensions()
    const navigate = useNavigate()

    const [teachers, setTeachers] = useState([])
    const [filteredTeachers, setFilteredTeachers] = useState([])
    const [teachersLoading, setTeacherLoading] = useState([])

    const [open, setOpen] = useState(false)
    const [profilePic, setProfilePic] = useState(null)
    const [email, setEmail] = useState("")
    const [name, setName] = useState("")
    const [surname, setSurname] = useState("")
    const [qualification, setQualification] = useState("")
    const [bio, setBio] = useState("")
    const [status, setStatus] = useState(AccountStatus.Active)
    const [tagsList, setTagList] = useState([])
    const [tags, setTags] = useState([])
    const [options, setOptions] = useState([])
    const [imgFile, setImgFile] = useState(null)
    const [cropMode, setCropMode] = useState(false)
    const [addStatus, setAddStatus] = useState(null)
    const [isTeacherValid, setIsTeacherValid] = useState(false)
    const [page, setPage] = useState(0)
    const [per, setPer] = useState(25)
    const [count, setCount] = useState(null)
    const [q, setQ] = useState(null)
    const [showPagination, setShowPagination] = useState(true)

    const roleName = useMemo(() => role === Role.Teacher ? "docente" : "tutor", [role])
    const roleNamePlural = useMemo(() => role === Role.Teacher ? "docenti" : "tutor", [role])

    const dropzoneRef = useRef(null)
    useEffect(() => {
        const getTags = async (teacher) => {
            try {
                const tags = await api.get(`/admin/tags?type=general`)

                setTagList(tags)
                setOptions(tags)
            } catch (e) {
                console.error(e)
            }
        }

        getTeachers()
        getTags()
    }, [role])

    useEffect(() => {
        setTeachers(t => {
            t.sort((a, b) => sortUser(a, b, orderBy, sortDirection))
            return [...t]
        })
        setFilteredTeachers((t) => {
            t.sort((a, b) => sortUser(a, b, orderBy, sortDirection))
            return [...t]
        })
    }, [orderBy, sortDirection])

    useEffect(() => {
        const newOptions = tagsList.filter((tl) => {
            return tags.map(t => t.id).includes(tl.id) === false
        })
        setOptions(newOptions)
    }, [tags])

    useEffect(() => {
        setIsTeacherValid(
            profilePic !== null &&
            name !== "" &&
            surname !== "" &&
            email !== "" &&
            email.match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/) &&
            qualification !== ""
        )
    }, [profilePic, name, surname, email, qualification])

    const getTeachers = useCallback(async (q = null) => {

        setTeacherLoading(true)
        try {
            let route = q ?
                `/admin/teachers?q=${q}&orderBy=${sortDirection}${orderBy.value}&role=${role}&complete=true` :
                `/admin/teachers?page=${page}&per=${per}&orderBy=${sortDirection}${orderBy.value}&role=${role}&complete=true`

            const data = await api.get(route)
            const { teachers, count } = data
            setTeachers(teachers)
            setCount(count)
        } catch (e) {
            console.error(e)
        }
        setTeacherLoading(false)
    }, [page, per, orderBy, sortDirection])

    useEffect(() => {
        getTeachers(q)
    }, [page, per, orderBy, sortDirection])

    const addTeacher = async () => {
        setAddStatus(ButtonStatus.Loading)
        try {
            const formData = new FormData()
            formData.append("name", name)
            formData.append("surname", surname)
            formData.append("email", email)
            formData.append("qualification", qualification)
            formData.append("description", bio)
            formData.append("status", status)
            formData.append("role", role)
            formData.append("tags", JSON.stringify(tags.map(t => t.id)))

            if (profilePic) {
                let blob = await fetch(profilePic).then(r => r.blob());
                formData.append("picture", blob);
            }

            await api.post("/admin/teachers", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            getTeachers()
            setAddStatus(ButtonStatus.Success)
            setOpen(false)
            setProfilePic(null)
            setName("")
            setSurname("")
            setEmail("")
            setQualification("")
            setBio("")
            setTags([])
            setOptions(tagsList)
            setAddStatus(null)
        } catch (e) {
            console.error(e)
            setAddStatus(ButtonStatus.Error)
        }
    }

    const onAddClick = () => setOpen(true)

    useImperativeHandle(ref, () => {
        return {
            onAddClick
        }
    })

    const onSearch = useCallback(async (text) => {
        if (text && text.length >= 3) {
            setShowPagination(false)
            getTeachers(text)
            return;
        }

        if (!text) {
            setShowPagination(true)
            setPage(0)
            getTeachers()
        }
    }, [getTeachers])

    const getTeacherStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <StatusBadge appearance="soft" label={"ATTIVO"} color={"var(--sf-green)"} />
            case 'unverified':
                return <StatusBadge appearance="soft" label={"NESSUN ACCESSO"} color={"var(--sf-red)"} />
            case 'suspended':
                return <StatusBadge appearance="soft" label={"SOSPESO"} color={"var(--sf-orange)"} />
            default:
                return null
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.section}>
                <div className={styles.sectionInner}>
                    <div className={styles.teachers}>
                        <div className={styles.searchBar}>
                        <TextInput
                            onKeyUp={(value) => {
                                setQ(value !== '' ? value : null)
                                onSearch(value)
                            }}
                            type="search" appearance="modern"
                            placeholder={t("search")}
                            style={{
                                backgroundColor: "var(--background-secondary-color)",

                            }} />
                        </div>
                        <Table appearance="minimal" style={{ borderRadius: 0, marginTop: 0 }} columns={[
                            { key: 'id', label: 'ID', type: 'number' },
                            { key: 'name', label: 'NOME', type: 'string' },
                            { key: 'surname', label: 'COGNOME', type: 'string' },
                            { key: 'email', label: 'EMAIL', type: 'string' },
                            { key: 'phone', label: 'TELEFONO', type: 'string' },
                            { key: 'status', label: 'STATO', type: 'custom' },
                            { key: 'registered_at', label: 'REGISTRATO IL', type: 'datetime' },
                            { key: 'last_access', label: 'ULTIMO ACCESSO', type: 'datetime' },
                            { key: 'courses', label: 'CORSI', type: 'custom' }
                        ]}
                            loading={teachersLoading}
                            elements={teachers.map(t => ({
                                ...t,
                                status: { value: t.status, type: 'string', component: getTeacherStatusBadge(t.status) },
                                courses: {
                                    value: t.courses.length, type: 'number', component: t.courses.length > 0 ? <TooltipWrapper
                                        component={
                                            <div className={styles.coursesTooltip}>
                                                <Each of={t.courses} render={(c) => (
                                                    <div className={styles.course}>
                                                        {c.name}
                                                    </div>
                                                )} />
                                            </div>
                                        }
                                    >
                                        <div className={styles.courses}>
                                            {t.courses.length} Corsi
                                        </div>
                                    </TooltipWrapper> : <div>Nessun corso</div>
                                },
                                href: `/users/teachers/${t.id}`
                            }))}
                            defaultSort={{ key: 'registered_at', direction: 'desc' }}
                        />
                        {showPagination &&
                            <div className={styles.pagination}><Pagination total={count} per={per} page={page} group={width > 540 ? 5 : 0} onChange={p => setPage(p)} loading={teachersLoading} /></div>
                        }
                    </div>
                </div>

            </div>
            <Dialog
                appearance="modern"
                style={{ maxWidth: 840 }}
                open={open}
                onClose={() => {
                    setOpen(false)
                    setProfilePic(null)
                    setName("")
                    setSurname("")
                    setEmail("")
                    setQualification("")
                    setBio("")
                    setTags([])
                    setOptions(tagsList)
                    setAddStatus(null)
                }}
                title={`Aggiungi ${roleName}`}
            >
                <div className={formStyles.dialog}>
                    <div className={formStyles.imageSelector}>
                        {!cropMode && !profilePic &&
                            <Dropzone
                                onDrop={(files) => {
                                    if (files[0]) {
                                        setImgFile(files[0])
                                        setCropMode(true)
                                    }
                                }}
                                accept={{
                                    'image/jpeg': [],
                                    'image/png': [],
                                    'image/heic': []
                                }}
                            >
                                {({ getRootProps, getInputProps }) => (
                                    <section style={{ display: 'flex', width: '100%', }}>
                                        <div {...getRootProps()} style={{ display: 'flex', height: "100%", alignItems: "center", width: '100%' }}>
                                            <input {...getInputProps()} ref={dropzoneRef} />
                                            <div className={formStyles.editPictureDropzone}>
                                                <InsertImageIllustration></InsertImageIllustration>
                                                <div className={formStyles.editPictureLabel}>
                                                    {t('profile.dropImage')}
                                                </div>
                                                <Button
                                                    style={{ marginTop: '.5rem', padding: '0.6rem 2rem' }}
                                                    appearance="subtle" accentColor="var(--primary)"
                                                    onClick={() => dropzoneRef.current?.click()}
                                                >
                                                    {capitalize(t('profile.chooseImage'))}
                                                </Button>
                                            </div>
                                        </div>
                                    </section>
                                )}
                            </Dropzone>
                        }
                        {cropMode && !profilePic &&
                            <>
                                <ImageCropper
                                    src={URL.createObjectURL(imgFile)}
                                    onSave={(image) => {
                                        setProfilePic(image)
                                        setCropMode(false)
                                    }}
                                    onCancel={() => { setCropMode(false) }}
                                    compression={{ quality: 0.7, maxWidth: 300, maxHeight: 300 }}
                                >
                                </ImageCropper>
                            </>
                        }
                        {
                            profilePic &&
                            <div className={formStyles.profilePicPreview}>
                                <img className={formStyles.profilePic} src={profilePic} alt='' />
                                <Button
                                    style={{ padding: "0.7rem 1.5rem" }}
                                    appearance="subtle" accentColor="var(--primary)"
                                    onClick={() => {
                                        setProfilePic(null)
                                        setCropMode(false)
                                    }}
                                >
                                    <EditIcon />
                                    Cambia
                                </Button>
                            </div>
                        }
                    </div>
                    <div className={formStyles.inputs}>
                        <TextInput appearance="modern" value={name} type="text" label={capitalize(t('profile.name'))} placeholder={capitalize(t('profile.name'))} onKeyUp={v => setName(v)}></TextInput>
                        <TextInput appearance="modern" value={surname} type="text" label={capitalize(t('profile.surname'))} placeholder={capitalize(t('profile.surname'))} onKeyUp={v => setSurname(v)}></TextInput>
                        <TextInput appearance="modern" value={email} type="text" label={capitalize(t('profile.email'))} placeholder={capitalize(t('profile.email'))} onKeyUp={v => setEmail(v.replaceAll(' ', ''))} />
                        <TextInput appearance="modern" value={qualification} type="text" label={capitalize(t('profile.qualification'))} placeholder={capitalize(t('profile.qualification'))} onKeyUp={v => setQualification(v)} />
                        <label className={formStyles.field}><span className={formStyles.label}>Biografia</span><TextareaAutosize
                            value={bio}
                            minRows={3} maxRows={10}
                            type="text"
                            placeholder={capitalize(t("profile.bio"))}
                            className={formStyles.textArea}
                            onChange={e => setBio(e.target.value)} /></label>

                        <div className={formStyles.field}><span className={formStyles.label}>Competenze</span>
                            <TagSelector options={options} selected={tags} onAdd={tag => setTags(current => [...current, tag])} onRemove={tag => setTags(current => current.filter(item => item.id !== tag.id))} />
                        </div>
                        <div className={formStyles.bottom}>
                            <div className={formStyles.switch}>
                                <button type="button" aria-pressed={status === AccountStatus.Active} className={status === AccountStatus.Active ? `${formStyles.status} ${formStyles.active}` : `${formStyles.status} ${formStyles.active} ${formStyles.notSelected}`}
                                    onClick={() => setStatus(AccountStatus.Active)}>Attivo</button>
                                <button type="button" aria-pressed={status === AccountStatus.Suspended} className={status === AccountStatus.Suspended ? `${formStyles.status} ${formStyles.suspended}` : `${formStyles.status} ${formStyles.suspended} ${formStyles.notSelected}`}
                                    onClick={() => setStatus(AccountStatus.Suspended)}>Sospeso</button>
                            </div>

                            <Button
                                style={{ padding: '.65rem 1rem', borderRadius: 8, fontSize: '.75rem' }}
                                accentColor={"var(--primary)"}
                                disabled={isTeacherValid === false || addStatus === ButtonStatus.Loading}
                                status={addStatus}
                                onClick={addTeacher}
                            >
                                Aggiungi
                                {
                                    addStatus === null &&
                                    <AddIcon />
                                }
                            </Button>
                        </div>
                    </div>
                </div>
            </Dialog>
        </div >
    )
})

export default Teachers
